import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Camera, MapPin, Video, Loader2, Printer } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { ACTIVITY_TYPES, EVIDENCE, ROLE_HOME, PROJECT_TYPES, formatDay, formatDate, mediaSrc } from "../../lib/labels";
import { StatusBadge, EmptyState } from "../dash/ui";

export const PHASES = [
  ["PERSIAPAN", "Persiapan Lahan", ["SURVEI_LAHAN", "PEMBERSIHAN_LAHAN", "PENATAAN_LAHAN", "PEMBUATAN_DRAINASE", "PEMBUATAN_LUBANG_TANAM", "PERSIAPAN_LAHAN", "PERBAIKAN_INFRASTRUKTUR", "PERBAIKAN"]],
  ["PENANAMAN", "Penanaman", ["PENGADAAN_BIBIT", "PENANAMAN_KOPI", "PENANAMAN", "PENYULAMAN", "PEMASANGAN_NAUNGAN"]],
  ["PERAWATAN", "Perawatan", ["PEMUPUKAN", "PENYIANGAN_GULMA", "PENYIANGAN", "PEMANGKASAN", "PENGENDALIAN_HAMA", "PENGENDALIAN_PENYAKIT", "PERAWATAN_TANAMAN", "PERAWATAN", "PENYEMPROTAN", "IRIGASI", "INSPEKSI_KEBUN", "INSPEKSI"]],
  ["PEMBUNGAAN", "Pembungaan & Buah", ["PENGAMATAN_PEMBUNGAAN", "PENGAMATAN_BUAH"]],
  ["PANEN", "Panen", ["PANEN_CHERRY", "PANEN", "SORTASI_CHERRY"]],
  ["PASCAPANEN", "Pascapanen", ["PASCAPANEN", "PENGERINGAN", "HULLING", "SORTASI_GREEN_BEAN", "GRADING", "PENGEMASAN"]],
  ["LAINNYA", "Kegiatan Lain", ["KEGIATAN_LAIN"]],
];
const phaseOf = (t) => (PHASES.find(([, , types]) => types.includes(t)) || PHASES[PHASES.length - 1])[0];
export const MONTHS = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

const Thumb = ({ m, eager }) => (
  <a href={mediaSrc(m)} target="_blank" rel="noreferrer" className="relative block aspect-square rounded-lg overflow-hidden border bg-slate-100" data-testid={`history-media-${m.id}`}>
    {m.type === "VIDEO"
      ? <><video src={`${mediaSrc(m)}#t=0.1`} preload="metadata" playsInline muted className="h-full w-full object-cover" /><span className="absolute inset-0 grid place-items-center bg-black/30"><Video className="h-6 w-6 text-white" /></span></>
      : <img src={mediaSrc(m)} alt={m.caption || EVIDENCE[m.category] || "Foto bukti"} loading={eager ? "eager" : "lazy"} className="h-full w-full object-cover" />}
    {(m.caption || EVIDENCE[m.category]) && <span className="absolute bottom-0 inset-x-0 bg-black/55 px-1.5 py-0.5 text-[10px] text-white truncate" title={m.caption || ""}>{m.caption || EVIDENCE[m.category]}</span>}
  </a>
);

const Entry = ({ a, base, eager }) => (
  <li className="relative pl-5 border-l-2 border-emerald-200 pb-6 last:pb-0" data-testid={`history-entry-${a.id}`}>
    <span className="absolute -left-[7px] top-1 h-3 w-3 rounded-full bg-emerald-700" />
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm font-bold text-slate-900">{formatDay(a.activityDate)}</span>
      <Link to={`${base}/kegiatan/${a.id}`} className="text-sm font-semibold text-emerald-800 hover:underline">{ACTIVITY_TYPES[a.activityType] || a.activityType}</Link>
      <StatusBadge value={a.status} />
    </div>
    <p className="mt-1 text-xs text-slate-500 flex flex-wrap gap-x-3">
      <span>{a.farmerGroupName} × PT SJI</span>
      <span className="inline-flex items-center gap-1"><Camera className="h-3 w-3" />{a.media.filter((m) => m.type === "PHOTO").length} foto · {a.media.filter((m) => m.type === "VIDEO").length} video</span>
      <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{a.gps ? "GPS terekam" : "Tanpa GPS"}</span>
    </p>
    <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">{a.media.map((m) => <Thumb key={m.id} m={m} eager={eager} />)}</div>
  </li>
);

export const BeforeAfter = ({ projectId }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    const get = (latest) => api.get(`/projects/${projectId}/photo-history`, { params: { limit: 3, latest } }).then((r) => r.data);
    Promise.all([get(false), get(true)]).then(([a, b]) => setRows([a, b])).catch(() => setRows([[], []]));
  }, [projectId]);
  if (!rows) return null;
  const pick = (list) => list.flatMap((a) => a.media.filter((m) => m.type === "PHOTO").map((m) => ({ ...m, a })));
  const first = pick(rows[0])[0];
  const lastList = pick(rows[1]);
  const last = lastList.filter((m) => m.a.id === rows[1][0]?.id).pop() || lastList[0];
  const photos = first && last && first.id !== last.id ? [first, last] : [];
  if (photos.length < 2) return <p className="text-xs text-slate-500" data-testid={`before-after-empty-${projectId}`}>Perbandingan sebelum-sesudah tampil setelah ada minimal dua foto bukti dari kelompok tani.</p>;
  const pair = [["SEBELUM", photos[0]], ["TERBARU", photos[1]]];
  return (
    <div className="grid grid-cols-2 gap-2" data-testid={`before-after-${projectId}`}>
      {pair.map(([label, m]) => (
        <figure key={label} className="min-w-0">
          <div className="relative aspect-[4/3] rounded-lg overflow-hidden border bg-slate-100">
            <img src={mediaSrc(m)} alt={`${label} ${ACTIVITY_TYPES[m.a.activityType] || ""}`} className="h-full w-full object-cover" />
            <span className="absolute top-1.5 left-1.5 rounded bg-black/60 px-2 py-0.5 text-[10px] font-bold text-white">{label}</span>
          </div>
          <figcaption className="mt-1 text-[11px] text-slate-600 truncate">{formatDay(m.a.activityDate)} · {ACTIVITY_TYPES[m.a.activityType]}</figcaption>
        </figure>
      ))}
    </div>
  );
};

export const PrintPhotoReport = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [search] = useSearchParams();
  const year = search.get("tahun");
  const month = search.get("bulan");
  const type = search.get("jenis");
  const [p, setP] = useState(null);
  useEffect(() => { api.get(`/projects/${id}`).then((r) => setP(r.data)).catch(() => setP(false)); }, [id]);
  if (p === null) return <Loader2 className="h-6 w-6 animate-spin text-emerald-700" />;
  if (!p) return <EmptyState text="Kebun tidak ditemukan atau Anda tidak memiliki akses." testid="print-not-found" />;
  return (
    <div className="space-y-5 print-area" data-testid="print-photo-report">
      <div className="flex flex-wrap gap-2" data-print-hide>
        <button type="button" onClick={() => window.print()} className="inline-flex items-center gap-1.5 h-11 px-5 rounded-full bg-emerald-800 text-white font-bold" data-testid="print-btn"><Printer className="h-4 w-4" />Cetak / Simpan sebagai PDF</button>
        <Link to={`${ROLE_HOME[user.role]}/arsip-foto?proyek=${id}`} className="inline-flex items-center h-11 px-5 rounded-full border font-semibold text-emerald-800" data-testid="print-back">Kembali</Link>
      </div>
      <header className="border-b pb-4">
        <p className="text-xs font-bold uppercase tracking-wide text-amber-700" data-testid="print-period">Laporan Foto Kebun Kopi — Salimbado Farm · {year ? `Tahun ${year}` : "Semua Tahun"}{month && ` · ${MONTHS[month - 1]}`}{type && ` · ${ACTIVITY_TYPES[type] || type}`}</p>
        <h1 className="text-2xl font-extrabold text-slate-900">{p.projectCode} · {p.name}</h1>
        <p className="text-sm text-slate-600">{PROJECT_TYPES[p.projectType] || PROJECT_TYPES.INVESTMENT_COFFEE_PROJECT} · Lahan {p.land?.landCode} · {p.land?.areaHectares} ha</p>
        <p className="text-sm text-slate-600">Pengelola: {p.farmerGroupName} · PT SJI: verifikasi & monitoring</p>
        <p className="text-xs text-slate-500">Dicetak {formatDate(new Date().toISOString())} oleh {user.name}. Foto adalah bukti asli unggahan kelompok tani.</p>
      </header>
      <PhotoHistory projectId={id} base={ROLE_HOME[user.role]} year={year} month={month} type={type} all />
    </div>
  );
};

const PAGE = 20;

const Phases = ({ rows, base, prefix, eager }) => {
  const present = PHASES.map(([k, l], n) => [k, l, rows.filter((a) => phaseOf(a.activityType) === k), n + 1]).filter(([, , list]) => list.length);
  return <>
    <ol className="flex flex-wrap gap-1.5 text-[11px]" data-print-hide data-testid={`history-phase-nav${prefix ? `-${prefix.slice(0, 4)}` : ""}`}>
      {present.map(([k, l, list]) => <li key={k}><a href={`#phase-${prefix}${k}`} className="rounded-full bg-white border px-2.5 py-1 font-semibold text-emerald-900">{l} ({list.length})</a></li>)}
    </ol>
    {present.map(([k, l, list, num]) => (
      <section key={k} id={`phase-${prefix}${k}`} className="bg-white border rounded-xl p-4 sm:p-5" data-testid={`history-phase-${prefix}${k}`}>
        <h3 className="text-sm font-bold uppercase tracking-wide text-slate-900"><span className="text-amber-700 font-mono mr-2">TAHAP {num}</span>{l}</h3>
        <p className="text-xs text-slate-500 mb-4">{formatDay(list[0].activityDate)}{list.length > 1 && ` – ${formatDay(list[list.length - 1].activityDate)}`}</p>
        <ol>{list.map((a) => <Entry key={a.id} a={a} base={base} eager={eager} />)}</ol>
      </section>
    ))}
  </>;
};

export const PhotoHistory = ({ projectId, base, year, month, type, all }) => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(false);
  const [more, setMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = (skip) => {
    setBusy(true);
    return api.get(`/projects/${projectId}/photo-history`, { params: { year: year || undefined, month: month || undefined, activityType: type || undefined, skip, limit: all ? 0 : PAGE } })
      .then((r) => { setRows((prev) => (skip ? [...prev, ...r.data] : r.data)); setMore(!all && r.data.length === PAGE); })
      .catch(() => setError(true)).finally(() => setBusy(false));
  };
  useEffect(() => { setRows(null); setError(false); load(0); }, [projectId, year, month, type]); // eslint-disable-line react-hooks/exhaustive-deps
  if (error) return <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="history-error">Riwayat foto gagal dimuat. Muat ulang halaman untuk mencoba lagi.</p>;
  if (!rows) return <Loader2 className="h-6 w-6 animate-spin text-emerald-700" />;
  if (!rows.length) return <EmptyState text={month || type ? "Tidak ada foto yang cocok dengan pencarian ini." : "Belum ada foto bukti kebun kopi yang dilaporkan untuk kebun ini."} testid="history-empty" />;
  const query = new URLSearchParams(Object.entries({ tahun: year, bulan: month, jenis: type }).filter(([, v]) => v)).toString();
  const years = year ? [String(year)] : [...new Set(rows.map((a) => a.activityDate.slice(0, 4)))];
  return (
    <div className="space-y-5" data-testid="photo-history">
      {!all && <Link to={`${base}/proyek/${projectId}/cetak-foto${query ? `?${query}` : ""}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-800 border rounded-full px-4 py-2" data-print-hide data-testid="history-print-link"><Printer className="h-4 w-4" />Unduh / Cetak Laporan Foto (PDF)</Link>}
      <p className="text-xs text-slate-500 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">Seluruh foto, video, dan catatan foto di sini diunggah dan ditulis oleh kelompok tani / koperasi pengelola dari lapangan, bukan foto ilustrasi. PT SJI memantau dan memberi saran.</p>
      {years.map((y) => (
        <div key={y} className="space-y-4" data-testid={`history-year-${y}`}>
          {years.length > 1 && <h2 className="text-lg font-extrabold text-slate-900 border-b pb-1">Tahun {y}</h2>}
          <Phases rows={rows.filter((a) => a.activityDate.startsWith(y))} base={base} prefix={years.length > 1 ? `${y}-` : ""} eager={all} />
        </div>
      ))}
      {more && <button type="button" disabled={busy} onClick={() => load(rows.length)} className="w-full h-11 rounded-full border bg-white font-semibold text-emerald-800" data-print-hide data-testid="history-load-more">{busy ? "Memuat…" : "Muat lebih banyak kegiatan"}</button>}
    </div>
  );
};
